import "../css/main.css";
import "./style.css";
import Menu from "../common/menu2.js";
import TopBar from "../common/topBar2.js";
import PaymentsTable from "./tables/payments.js";
import LastTransations from "./tables/last_transations.js";
import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useData } from "../../contexts/DataContext.js";
import { useAuth } from "../../contexts/AuthContext.js";
import { useLocation } from "react-router-dom";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import {
  Coins,
  Clock,
  AlertCircle,
  Wallet,
  Users,
  FileText,
  Clock3,
  CalendarDays,
  TrendingUp,
  TrendingDown,
} from "lucide-react";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const iconColorClasses = {
  blue: "bg-blue-50 text-blue-700",
  orange: "bg-orange-50 text-orange-700",
  red: "bg-red-50 text-red-700",
  green: "bg-green-50 text-green-700",
};

const statIcons = {
  blue: <Coins size={24} />,
  orange: <Clock size={24} />,
  red: <AlertCircle size={24} />,
  green: <Wallet size={24} />,
};

const miniStatIcons = {
  blue: <Users size={20} />,
  green: <FileText size={20} />,
  orange: <Clock3 size={20} />,
  red: <AlertCircle size={20} />,
};

const gradientAccents = {
  blue: "from-blue-500 via-blue-400 to-cyan-400",
  orange: "from-orange-500 via-amber-400 to-yellow-400",
  red: "from-red-500 via-rose-400 to-pink-400",
  green: "from-emerald-500 via-green-400 to-teal-400",
};

const ringAccents = {
  blue: "ring-blue-100",
  orange: "ring-orange-100",
  red: "ring-red-100",
  green: "ring-green-100",
};

const trendBadges = {
  blue: {
    icon: <TrendingUp size={12} />,
    text: "Financiamento",
    cls: "bg-blue-100 text-blue-700",
  },
  orange: {
    icon: <Clock size={12} />,
    text: "A receber",
    cls: "bg-orange-100 text-orange-700",
  },
  red: {
    icon: <TrendingDown size={12} />,
    text: "Em risco",
    cls: "bg-red-100 text-red-700",
  },
  green: {
    icon: <TrendingUp size={12} />,
    text: "Disponível",
    cls: "bg-green-100 text-green-700",
  },
};

const StatCard = ({ title, value, sub, color, loading }) => {
  const bg = iconColorClasses[color] || iconColorClasses.blue;
  const icon = statIcons[color] || statIcons.blue;
  const gradient = gradientAccents[color] || gradientAccents.blue;
  const ring = ringAccents[color] || ringAccents.blue;
  const badge = trendBadges[color] || trendBadges.blue;

  return (
    <div
      className={`group relative flex h-full flex-col overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ${ring} transition-all duration-300 hover:shadow-md active:scale-[0.99] sm:ring-slate-200/50 md:min-h-0 max-md:min-h-[150px]`}
    >
      {/* Mobile gradient top bar */}
      <div
        className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${gradient} sm:hidden`}
      />

      {/* Mobile decorative blurred blob */}
      <div
        className={`pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full bg-gradient-to-br ${gradient} opacity-10 blur-2xl sm:hidden`}
      />

      <div className="relative flex flex-1 flex-col p-4 sm:p-5">
        <div className="flex flex-1 items-start justify-between gap-3 md:flex-none">
          <div className="flex min-w-0 flex-1 flex-col">
            {/* Mobile badge */}
            <span
              className={`mb-2 inline-flex w-fit items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide sm:hidden ${badge.cls}`}
            >
              {badge.icon}
              {badge.text}
            </span>

            <p className="text-[11px] font-medium uppercase tracking-wider text-slate-400">
              {title}
            </p>

            <div className="mt-1 flex flex-1 flex-col md:flex-none">
              {loading ? (
                <div className="h-7 w-32 animate-pulse rounded bg-slate-200"></div>
              ) : (
                <span className="text-xl font-semibold text-slate-600 break-all sm:text-2xl">
                  {value}
                </span>
              )}
            </div>
          </div>

          <span
            className={`shrink-0 flex h-10 w-10 items-center justify-center rounded-xl ${bg} transition-transform duration-300 group-hover:scale-110 sm:group-hover:scale-100`}
          >
            {icon}
          </span>
        </div>

        {/* Sub pinned to bottom on mobile only; flows naturally on desktop */}
        <div className="mt-2 flex min-h-[24px] items-end md:mt-0 md:block md:min-h-0">
          {sub && !loading && (
            <span className="inline-flex w-fit items-center gap-1 rounded-md bg-slate-50 px-1.5 py-0.5 text-[11px] text-slate-500 break-all sm:bg-transparent sm:p-0 sm:text-xs sm:text-slate-400">
              {sub}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const MiniStat = ({ label, value, color }) => {
  const bg = iconColorClasses[color] || iconColorClasses.blue;
  const icon = miniStatIcons[color] || miniStatIcons.blue;

  return (
    <div className="flex items-center gap-3 rounded-xl bg-slate-50 px-4 py-2.5 transition hover:bg-slate-100">
      <span
        className={`shrink-0 flex h-7 w-7 items-center justify-center rounded-lg ${bg}`}
      >
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-xs text-slate-500">{label}</p>
        <p className="text-base font-semibold text-slate-800 truncate sm:text-lg">
          {value}
        </p>
      </div>
    </div>
  );
};

const SectionCard = ({ title, children, right }) => (
  <div className="flex h-full flex-col rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200/50 sm:p-5">
    <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
      <h2 className="text-[13px] font-semibold uppercase tracking-wider text-slate-700">
        {title}
      </h2>
      {right}
    </div>
    <div className="flex min-h-0 flex-1 flex-col">{children}</div>
  </div>
);

const EmptyState = ({ text }) => (
  <div className="flex flex-col items-center justify-center py-8 text-center text-slate-400">
    <CalendarDays className="mb-2 h-8 w-8 opacity-30" />
    <p className="text-sm">{text}</p>
  </div>
);

const ModernManager = () => {
  const data = useData();
  const { user, selectedMicrocredit } = useAuth();
  const { pathname } = useLocation();

  const required_data = ["dashboard"];

  const [updateFilters, setUpdateFilters] = useState(null);
  const [search, setSearch] = useState("");
  const [filterOptions, setFilterOptions] = useState([]);
  const initial_filters = { start_date: "", end_date: "" };
  const [filters, setFilters] = useState(initial_filters);
  const [topFilters, setTopFilters] = useState({
    filtered_contract_year: new Date().getFullYear(),
    filtered_contract_month: new Date().getMonth(),
  });

  const [selectedTransationsPerformance, setSelectedTransationsPerformance] =
    useState("week");

  const carouselRef = useRef(null);
  const cardRefs = useRef([]);
  const [activeDot, setActiveDot] = useState(0);

  const months = data.months || [];
  const years = useMemo(
    () =>
      (
        data._dashboard?.contractsYearsFromStart || [new Date().getFullYear()]
      ).map((y) => Number(y)),
    [data._dashboard?.contractsYearsFromStart],
  );

  const loaded = data._loaded?.includes("dashboard");
  const _d = data._dashboard;

  useEffect(() => {
    if (!user) return;
    data.handleLoaded("remove", required_data);
    data._get(required_data, {
      dashboard: {
        microcredit_id: selectedMicrocredit,
        search: search,
        ...topFilters,
        ...filters,
        ...data.getParamsFromFilters(filterOptions),
      },
    });
  }, [
    user,
    pathname,
    search,
    updateFilters,
    filters,
    selectedMicrocredit,
    topFilters,
    data.updateTable,
  ]);

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;

    const handleScroll = () => {
      const scrollLeft = el.scrollLeft;
      const cardWidth = el.clientWidth;
      const index = Math.round(scrollLeft / (cardWidth * 0.85 + 12));
      setActiveDot(Math.min(Math.max(index, 0), 3));
    };

    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToCard = useCallback((index) => {
    const el = carouselRef.current;
    const card = cardRefs.current[index];
    if (!el || !card) return;
    el.scrollTo({
      left: card.offsetLeft - el.offsetLeft,
      behavior: "smooth",
    });
    setActiveDot(index);
  }, []);

  const perf = _d?.transations?.transations_performance;
  const perfKey = selectedTransationsPerformance;

  const perfData = useMemo(() => {
    const inflows = perf?.[`${perfKey}_inflows`] || [];
    const outflows = perf?.[`${perfKey}_outflows`] || [];
    return {
      labels: inflows.map((i) => i.name),
      datasets: [
        {
          label: "Entradas",
          data: inflows.map((i) => i.total),
          backgroundColor: "rgba(59, 130, 246, 0.55)",
          borderColor: "rgb(37, 99, 235)",
          borderWidth: 1,
          borderRadius: 4,
          barThickness: 24,
        },
        {
          label: "Saídas",
          data: outflows.map((i) => i.total),
          backgroundColor: "rgba(251, 191, 36, 0.55)",
          borderColor: "rgb(245, 158, 11)",
          borderWidth: 1,
          borderRadius: 4,
          barThickness: 24,
        },
      ],
    };
  }, [perf, perfKey]);

  const profitExpected = _d?.contracts?.payments_profit?.expected || [];
  const profitAchieved = _d?.contracts?.payments_profit?.achieved || [];

  const profitData = useMemo(() => {
    return {
      labels: profitExpected.map((i) => i?.month?.slice(0, 3) || ""),
      datasets: [
        {
          label: "Previsto",
          data: profitExpected.map((i) => i.total),
          backgroundColor: "rgba(96, 165, 250, 0.55)",
          borderColor: "rgb(37, 99, 235)",
          borderWidth: 1,
          borderRadius: 4,
          barThickness: 24,
        },
        {
          label: "Obtido",
          data: profitAchieved.map((i) => i.total),
          backgroundColor: "rgba(37, 99, 235, 0.85)",
          borderColor: "rgb(37, 99, 235)",
          borderWidth: 1,
          borderRadius: 4,
          barThickness: 24,
        },
      ],
    };
  }, [_d?.contracts?.payments_profit]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    plugins: {
      legend: { display: true, position: "bottom" },
      tooltip: { mode: "index", intersect: false },
    },
    interaction: { mode: "nearest", axis: "x", intersect: false },
    scales: {
      x: { stacked: true, grid: { display: false } },
      y: { stacked: true, beginAtZero: true, grid: { display: false } },
    },
  };

  return (
    <>
      <main
        className={`dashboard-container ${
          data.loaded.includes("dashboard") ? "" : "loading"
        }`}
      >
        <div className="dashboard">
          <Menu />
          <div className="main-dashboard">
            <TopBar homePage={true} activePage={"Dashboard"} />
            <div className="center">
              <div className="mx-auto flex w-full max-w-screen-2xl flex-col gap-4 sm:gap-5">
                {/* === Period selectors (mobile only) === */}
                <div className="-mx-1 flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs text-slate-600 shadow ring-1 ring-slate-200/50 md:hidden">
                  <span className="whitespace-nowrap text-xs text-slate-600">
                    Mês:
                  </span>
                  <select
                    value={topFilters.filtered_contract_month}
                    onChange={(e) =>
                      setTopFilters({
                        ...topFilters,
                        filtered_contract_month: Number(e.target.value),
                      })
                    }
                    className="w-full min-w-0 appearance-none rounded border border-slate-200 bg-transparent py-0.5 pl-2 pr-6 text-xs text-slate-800 outline-none"
                  >
                    {months.map((m, i) => (
                      <option key={i} value={i}>
                        {m}
                      </option>
                    ))}
                  </select>
                  <span className="whitespace-nowrap text-xs text-slate-600">
                    Ano:
                  </span>
                  <select
                    value={topFilters.filtered_contract_year}
                    onChange={(e) =>
                      setTopFilters({
                        ...topFilters,
                        filtered_contract_year: Number(e.target.value),
                      })
                    }
                    className="w-full min-w-0 appearance-none rounded border border-slate-200 bg-transparent py-0.5 pl-2 pr-6 text-xs text-slate-800 outline-none"
                  >
                    {years.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>

                {/* === Stat cards ===
                    Mobile: horizontal scroll-snap carousel (equal height via min-h + flex).
                    Desktop (md+): compact grid, natural height restored. */}
                <div className="relative md:mt-3">
                  <div
                    ref={carouselRef}
                    className="flex snap-x snap-mandatory items-stretch gap-3 overflow-x-auto pb-2 md:grid md:grid-cols-2 md:items-start md:gap-4 md:overflow-visible md:pb-0 xl:grid-cols-4"
                  >
                    <div
                      ref={(el) => (cardRefs.current[0] = el)}
                      className="relative flex min-w-[85%] shrink-0 snap-center md:min-w-0 md:shrink"
                    >
                      <div className="w-full">
                        <StatCard
                          title="Financiado"
                          value={
                            data._cn(
                              (
                                _d?.contracts_withnodatefilters
                                  ?.disbursementValue || 0
                              ).toFixed(2),
                            ) + " MT"
                          }
                          sub={`${months[
                            Number(topFilters.filtered_contract_month)
                          ]?.slice(0, 3)} - ${data._cn(
                            (_d?.contracts?.disbursementValue || 0).toFixed(2),
                          )} MT`}
                          color="blue"
                          loading={!loaded}
                        />
                      </div>
                      <div
                        className={`absolute -translate-y-[50%] top-0 left-4 hidden items-center gap-1.5 rounded-full bg-white px-3 py-1.5 shadow ring-1 ring-slate-200/50 md:flex ${
                          loaded ? "flex" : "hidden"
                        }`}
                      >
                        <span className="whitespace-nowrap text-xs text-slate-600">
                          Mês:
                        </span>
                        <select
                          value={topFilters.filtered_contract_month}
                          onChange={(e) =>
                            setTopFilters({
                              ...topFilters,
                              filtered_contract_month: Number(e.target.value),
                            })
                          }
                          className="w-full min-w-0 appearance-none rounded border border-slate-200 bg-transparent py-0.5 pl-2 pr-6 text-xs text-slate-800 outline-none"
                        >
                          {months.map((m, i) => (
                            <option key={i} value={i}>
                              {m}
                            </option>
                          ))}
                        </select>
                        <span className="whitespace-nowrap text-xs text-slate-600">
                          Ano:
                        </span>
                        <select
                          value={topFilters.filtered_contract_year}
                          onChange={(e) =>
                            setTopFilters({
                              ...topFilters,
                              filtered_contract_year: Number(e.target.value),
                            })
                          }
                          className="w-full min-w-0 appearance-none rounded border border-slate-200 bg-transparent py-0.5 pl-2 pr-6 text-xs text-slate-800 outline-none"
                        >
                          {years.map((y) => (
                            <option key={y} value={y}>
                              {y}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div
                      ref={(el) => (cardRefs.current[1] = el)}
                      className="flex min-w-[85%] shrink-0 snap-center md:min-w-0 md:shrink"
                    >
                      <div className="w-full">
                        <StatCard
                          title="Esperado"
                          value={
                            data._cn(
                              (
                                _d?.contracts_withnodatefilters?.expected || 0
                              ).toFixed(2),
                            ) + " MT"
                          }
                          sub={`${months[
                            Number(topFilters.filtered_contract_month)
                          ]?.slice(0, 3)} - ${data._cn(
                            (_d?.contracts?.expected || 0).toFixed(2),
                          )} MT`}
                          color="orange"
                          loading={!loaded}
                        />
                      </div>
                    </div>

                    <div
                      ref={(el) => (cardRefs.current[2] = el)}
                      className="flex min-w-[85%] shrink-0 snap-center md:min-w-0 md:shrink"
                    >
                      <div className="w-full">
                        <StatCard
                          title="Em falta"
                          value={
                            data._cn(
                              (
                                _d?.contracts_withnodatefilters?.left || 0
                              ).toFixed(2),
                            ) + " MT"
                          }
                          sub={`${months[
                            Number(topFilters.filtered_contract_month)
                          ]?.slice(0, 3)} - ${data._cn(
                            (_d?.contracts?.left || 0).toFixed(2),
                          )} MT`}
                          color="red"
                          loading={!loaded}
                        />
                      </div>
                    </div>

                    <div
                      ref={(el) => (cardRefs.current[3] = el)}
                      className="flex min-w-[85%] shrink-0 snap-center md:min-w-0 md:shrink"
                    >
                      <div className="w-full">
                        <StatCard
                          title="Saldo disponível"
                          value={
                            data._cn(
                              (
                                _d?.transations?.transations_performance
                                  ?.available_credit || 0
                              ).toFixed(2),
                            ) + " MT"
                          }
                          color="green"
                          loading={!loaded}

                          sub={'-'}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Mobile scroll dots — clickable */}
                  <div className="mt-2 flex justify-center gap-2 md:hidden">
                    {[0, 1, 2, 3].map((i) => (
                      <button
                        key={i}
                        type="button"
                        aria-label={`Ir para cartão ${i + 1}`}
                        onClick={() => scrollToCard(i)}
                        className={`h-2 rounded-full transition-all duration-300 ${
                          activeDot === i
                            ? "w-5 bg-blue-500"
                            : "w-2 bg-slate-300 hover:bg-slate-400"
                        }`}
                      />
                    ))}
                  </div>
                </div>

                {/* === Mini stats + upcoming payments === */}
                <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-3 lg:gap-6">
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 lg:col-span-1">
                    <MiniStat
                      label="Clientes"
                      value={_d?.total_clients}
                      color="blue"
                    />
                    <MiniStat
                      label="Gestores"
                      value={_d?.total_managers}
                      color="blue"
                    />
                    <MiniStat
                      label="Operadores"
                      value={_d?.total_operators}
                      color="blue"
                    />
                    <MiniStat
                      label="Contratos activos"
                      value={_d?.contracts?.approved_contracts}
                      color="green"
                    />
                    <MiniStat
                      label="Contratos pendentes"
                      value={_d?.contracts?.pending_contracts}
                      color="orange"
                    />
                    <MiniStat
                      label="Contratos em atraso"
                      value={_d?.contracts?.delayed_contracts}
                      color="red"
                    />
                  </div>

                  <div className="relative lg:col-span-2">
                    <div className="lg:absolute lg:inset-0">
                      <SectionCard title="Pagamentos próximos / em atraso">
                        {(
                          _d?.contracts?.delayed_and_upcomming_installents || []
                        ).length === 0 ? (
                          <EmptyState text="Sem registos" />
                        ) : (
                          <div className="max-h-[320px] overflow-y-auto lg:h-full lg:max-h-none lg:min-h-0">
                            <PaymentsTable
                              items={
                                _d?.contracts
                                  ?.delayed_and_upcomming_installents || []
                              }
                            />
                          </div>
                        )}
                      </SectionCard>
                    </div>
                  </div>
                </div>

                {/* === Performance chart + recent transactions === */}
                <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-2 xl:gap-6">
                  <SectionCard
                    title="Desempenho"
                    right={
                      <select
                        value={selectedTransationsPerformance}
                        onChange={(e) =>
                          setSelectedTransationsPerformance(e.target.value)
                        }
                        className="rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-700 outline-none"
                      >
                        <option value="week">Semanal</option>
                        <option value="month">Mensal</option>
                        <option value="year">Anual</option>
                      </select>
                    }
                  >
                    <div className="h-[240px] w-full sm:h-[280px]">
                      <Bar data={perfData} options={chartOptions} />
                    </div>
                  </SectionCard>

                  <SectionCard title="Transações recentes">
                    {(_d?.transations?.latest || []).length === 0 ? (
                      <EmptyState text="Sem registos" />
                    ) : (
                      <div className="max-h-[240px] overflow-y-auto sm:max-h-[280px]">
                        <LastTransations
                          items={_d?.transations?.latest || []}
                        />
                      </div>
                    )}
                  </SectionCard>
                </div>

                {/* === Profit chart === */}
                <SectionCard title="Lucro previsto e obtido">
                  <div className="h-[240px] w-full sm:h-[280px]">
                    {profitExpected.length === 0 &&
                    profitAchieved.length === 0 ? (
                      <EmptyState text="Sem dados de lucro disponíveis" />
                    ) : (
                      <Bar data={profitData} options={chartOptions} />
                    )}
                  </div>
                </SectionCard>
                <div className="max-md:h-[35px]"></div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default ModernManager;